import ExploreIcon from "@mui/icons-material/Explore";
import LayersIcon from "@mui/icons-material/Layers";
import {
  Drawer,
  Grid,
  IconButton,
  Slide,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import PinIcon from "mdi-react/PinIcon";
import PinOffIcon from "mdi-react/PinOffIcon";
import { Map } from "ol";
import React, { useEffect, useState } from "react";
import { If } from "react-extras";
import { useTranslation } from "react-i18next";
import {
  readMapSettings,
  updateMapSettings,
} from "../functions/localMapSettings";
import LayerSettings from "./LayerSettings";
import MapData from "./MapData";

const useStyles = makeStyles((theme) => ({
  drawerGrid: {
    flexBasis: theme.mapDrawer.width,
  },
  drawer: {
    backgroundColor: theme.palette.primary.light,
    width: theme.mapDrawer.width,
    maxWidth: theme.mapDrawer.maxWidth,
    overflowX: "hidden",
  },
  pinButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pinIcon: {
    fill: theme.palette.primary.contrastText,
  },
  topContainer: {
    boxShadow: theme.shadows[2],
  },
  tabs: {
    color: theme.palette.primary.contrastText,
  },
  tab: {
    margin: 0,
  },
  indicator: {
    "& > span": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  pinned: boolean;
  onPin: (isMedium?: boolean) => void;
  map: Map;
}

export interface Layer {
  title: string;
  group: string;
  visible: boolean;
  zIndex: number;
}

export type MapDrawerTabs = "layers" | "data";

const MapDrawer = ({
  open,
  onClose,
  map,
  pinned,
  onPin,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState<MapDrawerTabs>(
    readMapSettings("drawerTab"),
  );
  const { t } = useTranslation("map");

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const handleTabChange = (val: MapDrawerTabs) => {
    updateMapSettings({ drawerTab: val });
    setTabValue(val);
  };

  useEffect(() => {
    if (!isMedium) return;
    onPin(true);
  }, [isMedium, onPin]);

  return (
    <Grid item className={pinned ? classes.drawerGrid : ""}>
      <Drawer
        anchor="right"
        variant={pinned ? "permanent" : "temporary"}
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawer }}
        PaperProps={{ elevation: pinned ? 15 : undefined }}
      >
        <Grid container className={classes.topContainer}>
          <Grid item xs={10}>
            <Tabs
              value={tabValue}
              className={classes.tabs}
              TabIndicatorProps={{ className: classes.tab }}
              variant="fullWidth"
              indicatorColor="secondary"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              onChange={(_e, val) => handleTabChange(val)}
              textColor="inherit"
            >
              <Tab
                className={classes.tab}
                label={t("objects")}
                icon={<ExploreIcon />}
                value={"data" as MapDrawerTabs}
              />
              <Tab
                className={classes.tab}
                label={t("layers")}
                icon={<LayersIcon />}
                value={"layers" as MapDrawerTabs}
              />
            </Tabs>
          </Grid>

          {!isMedium && (
            <Grid item className={classes.pinButton} xs={2}>
              <Tooltip
                title={pinned ? t("unpin").toString() : t("pin").toString()}
              >
                <IconButton onClick={() => onPin()} size="large">
                  {pinned ? (
                    <PinOffIcon className={classes.pinIcon} />
                  ) : (
                    <PinIcon className={classes.pinIcon} />
                  )}
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
        <If condition={tabValue === "layers"}>
          <Slide in={tabValue === "layers"} direction="left">
            <LayerSettings map={map} />
          </Slide>
        </If>
        <If condition={tabValue === "data"}>
          <Slide in={tabValue === "data"} direction="right">
            <MapData map={map} />
          </Slide>
        </If>
      </Drawer>
    </Grid>
  );
};

export default MapDrawer;
